/**
 * Pure request builders for the document CRUD hooks (#2245).
 *
 * The builders make the ownership and routing decisions testable without a
 * React or network harness (the same shape as patients/update-patient-request):
 * every operation must carry tenant + patient identity in its path, and the
 * download route must pick the legacy-olly path exactly when the upload id
 * carries the legacy prefix.
 */

export const LEGACY_OLLY_UPLOAD_ID_PREFIX = "legacy_olly:";

export function getLegacyOllyDocumentIdFromUploadId(
  uploadId: string,
): string | null {
  if (!uploadId.startsWith(LEGACY_OLLY_UPLOAD_ID_PREFIX)) return null;
  return uploadId.slice(LEGACY_OLLY_UPLOAD_ID_PREFIX.length) || null;
}

export type DocumentIdentity = {
  tenantId: string;
  patientId: string | null;
};

export class MissingDocumentIdentityError extends Error {
  constructor(operation: string) {
    super(`Cannot ${operation}: missing tenantId or patientId`);
    this.name = "MissingDocumentIdentityError";
  }
}

export type DownloadUrlRequest =
  | {
      kind: "legacy-olly";
      path: "/{tenantId}/patients/{patientId}/legacy-documents/{legacyDocumentId}/download-url";
      params: {
        path: {
          tenantId: string;
          patientId: string;
          legacyDocumentId: string;
        };
        query?: { disposition: "inline" | "attachment" };
      };
    }
  | {
      kind: "upload";
      path: "/{tenantId}/uploads/{uploadId}/download-url";
      params: {
        path: { tenantId: string; uploadId: string };
        query?: { disposition: "inline" | "attachment" };
      };
    };

export function buildDownloadUrlRequest(
  identity: DocumentIdentity,
  uploadId: string,
  disposition?: "inline" | "attachment",
): DownloadUrlRequest {
  const query = disposition ? { disposition } : undefined;
  const legacyDocumentId = getLegacyOllyDocumentIdFromUploadId(uploadId);
  if (legacyDocumentId) {
    if (!identity.patientId) {
      throw new MissingDocumentIdentityError(
        "get legacy document download URL",
      );
    }
    return {
      kind: "legacy-olly",
      path: "/{tenantId}/patients/{patientId}/legacy-documents/{legacyDocumentId}/download-url",
      params: {
        path: {
          tenantId: identity.tenantId,
          patientId: identity.patientId,
          legacyDocumentId,
        },
        ...(query ? { query } : {}),
      },
    };
  }
  return {
    kind: "upload",
    path: "/{tenantId}/uploads/{uploadId}/download-url",
    params: {
      path: { tenantId: identity.tenantId, uploadId },
      ...(query ? { query } : {}),
    },
  };
}

export function buildDocumentMutationPath(
  identity: DocumentIdentity,
  operation: "update document" | "delete document",
  documentId: string,
): {
  path: "/{tenantId}/patients/{patientId}/documents/{documentId}";
  params: {
    path: { tenantId: string; patientId: string; documentId: string };
  };
} {
  if (!identity.tenantId || !identity.patientId) {
    throw new MissingDocumentIdentityError(operation);
  }
  return {
    path: "/{tenantId}/patients/{patientId}/documents/{documentId}",
    params: {
      path: {
        tenantId: identity.tenantId,
        patientId: identity.patientId,
        documentId,
      },
    },
  };
}
